import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useInView } from "react-intersection-observer";
import SEOHead from "../components/SEOHead";
import WhatsAppCTA from "../components/WhatsAppCTA";
import { serviceCatalogBySlug } from "../data/services";
import { useServiceBySlug, useLocationBySlug, useLocations } from "../hooks/useCMSData";
import { generateLocationServiceContent } from "../utils/contentGenerator";

const containerVariants = { hidden: {}, visible: { transition: { staggerChildren: 0.08 } } };
const itemVariants = { hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0, transition: { duration: 0.6 } } };

const Spinner = () => (
  <div style={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
    <div style={{ width: 32, height: 32, borderRadius: "50%", borderTop: "2px solid #D4A843", animation: "spin 0.8s linear infinite" }} />
    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
  </div>
);

export default function LocationServicePage() {
  const { serviceSlug = "", locationSlug = "" } = useParams();
  const { ref: processRef, inView: processInView } = useInView({ triggerOnce: true });
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);
  const [hoveredService, setHoveredService] = useState<string | null>(null);

  const { data: service, loading: serviceLoading } = useServiceBySlug(serviceSlug);
  const { data: location, loading: locationLoading } = useLocationBySlug(locationSlug);
  const { data: allLocations } = useLocations();

  if (serviceLoading || locationLoading) return <Spinner />;

  if (!service || !location) {
    return <div style={{ minHeight: "60vh", padding: "120px 24px", color: "#F8F4EC" }}>Page not found.</div>;
  }

  const content = generateLocationServiceContent(service, location);
  const relatedServices = content.relatedLocalServices.map((slug) => serviceCatalogBySlug[slug]).filter(Boolean);
  const nearbyCities = allLocations.filter((item) => item.countrySlug === location.countrySlug && item.slug !== location.slug).slice(0, 3);

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: content.faqs.map((faq) => ({ "@type": "Question", name: faq.q, acceptedAnswer: { "@type": "Answer", text: faq.a } })),
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: content.breadcrumbs.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.label,
      item: `https://www.obsidianweblabs.com${item.href}`,
    })),
  };

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    name: `Obsidian Web Labs - ${service.name} in ${location.city}`,
    description: content.metaDescription,
    url: content.canonicalUrl,
    telephone: "+916393387138",
    address: { "@type": "PostalAddress", addressLocality: "New Delhi", addressRegion: "Delhi", addressCountry: "IN" },
    areaServed: { "@type": "City", name: location.city, containedInPlace: { "@type": "Country", name: location.country } },
    serviceType: service.name,
    provider: { "@type": "Organization", name: "Obsidian Web Labs", url: "https://www.obsidianweblabs.com" },
  };

  return (
    <>
      <SEOHead title={content.metaTitle} description={content.metaDescription} canonicalUrl={content.canonicalUrl} schema={[serviceSchema, breadcrumbSchema, faqSchema]} />

      <section style={{
        background: `linear-gradient(135deg, #050505 0%, #0E0E0E 50%, #1A1208 100%)`,
        color: "#F8F4EC",
        padding: "120px 24px",
        position: "relative",
        overflow: "hidden",
      }}>
        {/* Animated background layers */}
        <div style={{
          position: "absolute",
          inset: 0,
          background: `radial-gradient(circle at ${Math.random() * 100}% ${Math.random() * 100}%, rgba(212,168,67,0.08) 0%, transparent 50%)`,
          pointerEvents: "none",
        }} />

        <motion.div style={{ maxWidth: 1200, margin: "0 auto", position: "relative", zIndex: 2 }} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8 }}>
          {/* Breadcrumb Navigation */}
          <motion.nav initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} style={{ marginBottom: 24, fontSize: 13, color: "#9B9387", display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            {content.breadcrumbs.map((item, idx) => (
              <div key={item.href} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Link to={item.href} style={{ color: "#D4A843", textDecoration: "none", transition: "color 0.3s" }} onMouseEnter={(e) => e.currentTarget.style.color = "#F8F4EC"} onMouseLeave={(e) => e.currentTarget.style.color = "#D4A843"}>
                  {item.label}
                </Link>
                {idx < content.breadcrumbs.length - 1 && <span style={{ color: "#908A83" }}>→</span>}
              </div>
            ))}
          </motion.nav>

          {/* Hero Section */}
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 0.8 }} style={{ marginBottom: 56 }}>
            <h1 style={{
              fontSize: "clamp(42px, 6vw, 72px)",
              fontFamily: "'Playfair Display', serif",
              fontWeight: 300,
              marginBottom: 20,
              lineHeight: 1.05,
              backgroundImage: "linear-gradient(135deg, #F8F4EC 0%, #D4A843 100%)",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}>
              {content.h1}
            </h1>
            <p style={{ maxWidth: 1000, color: "#C2BCB2", lineHeight: 1.8, fontSize: "clamp(16px, 2vw, 18px)" }}>
              {content.heroSubheadline}
            </p>
          </motion.div>

          {/* CTA Section */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 56 }}>
            <WhatsAppCTA serviceName={service.name} locationName={location.city} variant="hero" />
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.98 }}>
              <Link to="/contact" style={{
                color: "#F8F4EC",
                background: "rgba(212,168,67,0.15)",
                border: "1px solid rgba(212,168,67,0.3)",
                textDecoration: "none",
                padding: "14px 28px",
                borderRadius: 4,
                fontSize: 13,
                fontWeight: 600,
                letterSpacing: "0.08em",
                backdropFilter: "blur(8px)",
                display: "inline-block",
                transition: "all 0.3s ease",
              }} onMouseEnter={(e) => {
                e.currentTarget.style.background = "rgba(212,168,67,0.25)";
                e.currentTarget.style.borderColor = "rgba(212,168,67,0.5)";
              }} onMouseLeave={(e) => {
                e.currentTarget.style.background = "rgba(212,168,67,0.15)";
                e.currentTarget.style.borderColor = "rgba(212,168,67,0.3)";
              }}>
                CONTACT TEAM
              </Link>
            </motion.div>
          </motion.div>

          {/* Info Cards Grid */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
              gap: 20,
              marginBottom: 64,
            }}
          >
            <motion.article
              variants={itemVariants}
              whileHover={{ y: -4, transition: { duration: 0.3 } }}
              style={{
                background: "rgba(14,14,14,0.8)",
                border: "1px solid rgba(212,168,67,0.16)",
                borderRadius: 8,
                padding: 32,
                backdropFilter: "blur(10px)",
                cursor: "pointer",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
                <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#D4A843" }} />
                <h2 style={{ fontSize: 20, fontWeight: 600, color: "#D4A843", margin: 0 }}>Market Overview</h2>
              </div>
              <p style={{ color: "#B8B2A8", lineHeight: 1.8, margin: 0 }}>{content.introSection}</p>
            </motion.article>

            <motion.article
              variants={itemVariants}
              whileHover={{ y: -4, transition: { duration: 0.3 } }}
              style={{
                background: "rgba(26,18,8,0.8)",
                border: "1px solid rgba(212,168,67,0.24)",
                borderRadius: 8,
                padding: 32,
                backdropFilter: "blur(10px)",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
                <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#D4A843" }} />
                <h2 style={{ fontSize: 20, fontWeight: 600, color: "#D4A843", margin: 0 }}>Why {location.city}</h2>
              </div>
              <p style={{ color: "#B8B2A8", lineHeight: 1.8, margin: 0 }}>{content.marketAnalysis}</p>
            </motion.article>
          </motion.div>

          {/* Process Steps */}
          <motion.div ref={processRef} initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} style={{ marginBottom: 64 }}>
            <h2 style={{
              fontSize: "clamp(28px, 4vw, 42px)",
              fontFamily: "'Playfair Display', serif",
              fontWeight: 300,
              color: "#F8F4EC",
              marginBottom: 32,
            }}>
              How we work with {location.city} clients
            </h2>
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate={processInView ? "visible" : "hidden"}
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
                gap: 16,
              }}
            >
              {content.processSteps.map((step, idx) => (
                <motion.div
                  key={step}
                  variants={itemVariants}
                  whileHover={{ scale: 1.05 }}
                  style={{
                    background: "rgba(14,14,14,0.6)",
                    border: "1px solid rgba(212,168,67,0.12)",
                    borderRadius: 6,
                    padding: "20px 16px",
                    backdropFilter: "blur(10px)",
                    textAlign: "center",
                    position: "relative",
                  }}
                >
                  <div style={{
                    fontFamily: "'Bebas Neue', sans-serif",
                    fontSize: 24,
                    color: "#D4A843",
                    marginBottom: 12,
                    fontWeight: 700,
                  }}>
                    {idx + 1}
                  </div>
                  <p style={{ color: "#B8B2A8", fontSize: 13, lineHeight: 1.6, margin: 0 }}>{step}</p>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>

          {/* Related Services */}
          {relatedServices.length > 0 && (
            <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} style={{ marginBottom: 64 }}>
              <h2 style={{ fontSize: "clamp(28px, 4vw, 42px)", fontFamily: "'Playfair Display', serif", fontWeight: 300, color: "#F8F4EC", marginBottom: 24 }}>
                Complementary services in {location.city}
              </h2>
              <motion.div
                variants={containerVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12 }}
              >
                {relatedServices.map((svc) => (
                  <motion.div
                    key={svc.slug}
                    variants={itemVariants}
                    onMouseEnter={() => setHoveredService(svc.slug)}
                    onMouseLeave={() => setHoveredService(null)}
                    whileHover={{ y: -2 }}
                  >
                    <Link
                      to={`/services/${svc.slug}/${location.slug}`}
                      style={{
                        background: hoveredService === svc.slug ? "rgba(212,168,67,0.15)" : "rgba(14,14,14,0.6)",
                        border: `1px solid ${hoveredService === svc.slug ? "rgba(212,168,67,0.4)" : "rgba(212,168,67,0.12)"}`,
                        borderRadius: 6,
                        padding: "16px 12px",
                        color: "#F8F4EC",
                        textDecoration: "none",
                        display: "block",
                        backdropFilter: "blur(10px)",
                        textAlign: "center",
                        transition: "all 0.3s ease",
                        fontSize: 13,
                        fontWeight: 500,
                      }}
                    >
                      {svc.name}
                    </Link>
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>
          )}

          {/* FAQ Accordion */}
          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} style={{ marginBottom: 64 }}>
            <h2 style={{ fontSize: "clamp(28px, 4vw, 42px)", fontFamily: "'Playfair Display', serif", fontWeight: 300, color: "#F8F4EC", marginBottom: 32 }}>
              Common questions about {service.name} in {location.city}
            </h2>
            <div style={{ borderTop: "1px solid rgba(212,168,67,0.12)" }}>
              {content.faqs.map((faq, idx) => (
                <motion.div key={idx} layout style={{ borderBottom: "1px solid rgba(212,168,67,0.12)" }}>
                  <motion.button
                    onClick={() => setExpandedFaq(expandedFaq === idx ? null : idx)}
                    style={{
                      width: "100%",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "20px 0",
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      textAlign: "left",
                      color: "#F8F4EC",
                    }}
                    whileHover={{ paddingLeft: 8 }}
                  >
                    <span style={{ fontSize: 16, fontFamily: "'Playfair Display', serif", fontWeight: 400 }}>{faq.q}</span>
                    <motion.span
                      style={{ color: "#D4A843", fontSize: 20, minWidth: 24 }}
                      animate={{ rotate: expandedFaq === idx ? 45 : 0 }}
                    >
                      +
                    </motion.span>
                  </motion.button>
                  <AnimatePresence>
                    {expandedFaq === idx && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3 }}
                        style={{ overflow: "hidden", marginBottom: 20 }}
                      >
                        <p style={{ color: "#B8B2A8", lineHeight: 1.8, margin: 0 }}>{faq.a}</p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Nearby Cities */}
          {nearbyCities.length > 0 && (
            <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}>
              <h2 style={{ fontSize: "clamp(28px, 4vw, 42px)", fontFamily: "'Playfair Display', serif", fontWeight: 300, color: "#F8F4EC", marginBottom: 24 }}>
                We also serve these cities
              </h2>
              <motion.div
                variants={containerVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12 }}
              >
                {nearbyCities.map((city) => (
                  <motion.div key={city.slug} variants={itemVariants} whileHover={{ y: -2 }}>
                    <Link
                      to={`/${location.countrySlug}/${city.slug}`}
                      style={{
                        background: "rgba(14,14,14,0.6)",
                        border: "1px solid rgba(212,168,67,0.12)",
                        borderRadius: 6,
                        padding: "16px 12px",
                        color: "#F8F4EC",
                        textDecoration: "none",
                        display: "block",
                        backdropFilter: "blur(10px)",
                        textAlign: "center",
                        transition: "all 0.3s ease",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = "rgba(212,168,67,0.12)";
                        e.currentTarget.style.borderColor = "rgba(212,168,67,0.3)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = "rgba(14,14,14,0.6)";
                        e.currentTarget.style.borderColor = "rgba(212,168,67,0.12)";
                      }}
                    >
                      <div style={{ fontSize: 14, fontWeight: 500 }}>{city.city}</div>
                      <div style={{ fontSize: 11, color: "#8B8378", marginTop: 4 }}>{service.name}</div>
                    </Link>
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>
          )}
        </motion.div>
      </section>
    </>
  );
}
